/**
 * ── useTenantEntitlements ──
 * Resolves all module access for the current tenant (platform/association/org).
 * Combines the module registry, entitlement system, and tenant context into
 * a single, memoized query point.
 *
 * This is the aggregate hook — use useModuleAccess() for per-module checks.
 */

import { useMemo } from "react";
import { useEntitlementAccess, type EntitlementResult } from "@/hooks/useEntitlementAccess";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useOrg } from "@/contexts/OrgContext";
import {
  MODULE_REGISTRY,
  getModulesForTenant,
  type TenantType,
  type ModuleDefinition,
} from "@/config/moduleRegistry";

export interface TenantModuleState {
  definition: ModuleDefinition;
  entitlement: EntitlementResult;
  /** Resolved: does the current user have access right now? */
  hasAccess: boolean;
  /** Is this module eligible for the current tenant type? */
  isEligible: boolean;
}

export interface TenantEntitlements {
  /** Current resolved tenant type */
  tenantType: TenantType;
  /** All modules with resolved access state */
  modules: TenantModuleState[];
  /** Quick lookup: module key → resolved access */
  moduleMap: Map<string, TenantModuleState>;
  /** Modules the tenant has access to */
  activeModules: TenantModuleState[];
  /** Modules the tenant could upgrade to */
  lockedModules: TenantModuleState[];
  /** Core modules (always active) */
  coreModules: TenantModuleState[];
  /** Whether the user is a platform admin (full access) */
  isPlatformAdmin: boolean;
}

export function useTenantEntitlements(): TenantEntitlements {
  const { isMasterAdmin, isAssociationAdmin } = useIsAdmin();
  const { orgId } = useOrg();
  const { check } = useEntitlementAccess();

  return useMemo(() => {
    // Determine current tenant type
    const tenantType: TenantType = isMasterAdmin
      ? "platform"
      : isAssociationAdmin
        ? "association"
        : "org";

    const isPlatformAdmin = isMasterAdmin;
    const eligibleModules = getModulesForTenant(tenantType);

    // Resolve entitlement for each eligible module
    const modules: TenantModuleState[] = Object.values(MODULE_REGISTRY).map(
      (definition) => {
        const isEligible = definition.eligibleTenants.includes(tenantType);

        // Platform admins get full access
        if (isPlatformAdmin) {
          return {
            definition,
            entitlement: {
              hasAccess: true,
              accessState: "included" as const,
              behavior: "show" as const,
              moduleStatus: "active" as const,
              restrictedBy: null,
              availableAddOns: [],
              minPlan: undefined,
              isTemporary: false,
            },
            hasAccess: true,
            isEligible: true,
          };
        }

        // Core modules are always active for eligible tenants
        if (definition.isCore && isEligible) {
          return {
            definition,
            entitlement: {
              hasAccess: true,
              accessState: "included" as const,
              behavior: "show" as const,
              moduleStatus: "active" as const,
              restrictedBy: null,
              availableAddOns: [],
              minPlan: undefined,
              isTemporary: false,
            },
            hasAccess: true,
            isEligible,
          };
        }

        // Non-eligible modules are hidden
        if (!isEligible) {
          return {
            definition,
            entitlement: {
              hasAccess: false,
              accessState: "hidden" as const,
              behavior: "hide" as const,
              moduleStatus: null,
              restrictedBy: null,
              availableAddOns: [],
              minPlan: undefined,
              isTemporary: false,
            },
            hasAccess: false,
            isEligible: false,
          };
        }

        // Delegate to the existing entitlement system for non-core, eligible modules
        const entitlement = check(definition.key);
        return {
          definition,
          entitlement,
          hasAccess: entitlement.hasAccess,
          isEligible,
        };
      },
    );

    const moduleMap = new Map(modules.map((m) => [m.definition.key, m]));
    const activeModules = modules.filter((m) => m.hasAccess && m.isEligible);
    const lockedModules = modules.filter((m) => !m.hasAccess && m.isEligible);
    const coreModules = modules.filter((m) => m.definition.isCore && m.isEligible);

    return {
      tenantType,
      modules,
      moduleMap,
      activeModules,
      lockedModules,
      coreModules,
      isPlatformAdmin,
    };
  }, [isMasterAdmin, isAssociationAdmin, check]);
}
